import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  VStack,
  HStack,
} from '@everlywell/ui-kit';
import { CtaBanner } from 'components/CtaBanner';
import { graphql, Link, useStaticQuery } from 'gatsby';
import React from 'react';

import FacebookIcon from '../icons/FacebookIcon';
import InstagramIcon from '../icons/InstagramIcon';
import LinkedInIcon from '../icons/LinkedInIcon';
import Logo from '../icons/Logo';

const { WWW_ROOT } = require('utils/constants/urls');

interface MenuItem {
  title: string;
  submenu: {
    title: string;
    link: string;
  }[];
}

const menuItems: MenuItem[] = [
  {
    title: 'What we do',
    submenu: [
      { title: 'EverlyCare', link: '/everlycare' },
      {
        title: 'EverlyCare Annual',
        link: '/products/everlycare-annual-health-screenings',
      },
      { title: 'Diabetes', link: '/products/diabetes-testing-management' },
      { title: 'Heart health', link: '/products/heart-health-blood-tests' },
      {
        title: 'Colorectal cancer',
        link: '/products/colorectal-cancer-screening',
      },
    ],
  },
  {
    title: 'Who we serve',
    submenu: [
      { title: 'Employers', link: '/employers' },
      { title: 'Health Plans', link: '/health-plans' },
      { title: 'About Us', link: '/about-us' },
      { title: 'Careers', link: '/careers' },
    ],
  },
  {
    title: 'Resources',
    submenu: [
      { title: 'Resource Center', link: '/resources' },
      { title: 'For Patients', link: '/patients' },
    ],
  },
];

interface FooterProps {
  slug?: string;
}

const Footer: React.FC<FooterProps> = () => {
  const { allContentfulPage001, allContentfulCtaSection } =
    useStaticQuery(graphql`
      query {
        allContentfulPage001 {
          edges {
            node {
              slug
              metadata {
                description {
                  childMarkdownRemark {
                    rawMarkdownBody
                  }
                }
              }
            }
          }
        }
        allContentfulCtaSection(
          filter: { title: { eq: "Footer CTA Section" } }
        ) {
          nodes {
            ctaHeading: heading
            title
            text
            backgroundImage {
              file {
                url
              }
            }
            cta {
              label
              url
            }
            logo {
              file {
                url
              }
            }
            mobileBackgroundImage {
              file {
                url
              }
            }
          }
        }
      }
    `);

  const slug = typeof window !== 'undefined' ? window.location.pathname : '';

  const currentPage = allContentfulPage001.edges.find(
    ({ node }: { node: any }) => node.slug === slug,
  )?.node;

  return (
    <Box as="section" width="100%" py={[16, 20]}>
      <CtaBanner {...allContentfulCtaSection.nodes[0]} />
      <Box
        as="footer"
        width="100%"
        maxWidth="1440px"
        mx="auto"
        bg="white"
        pt={['2rem', '2rem']}
        pb={['1.25rem', '1.5rem', '2rem']}
        px="1rem"
      >
        <VStack spacing={['2rem', '2rem']} align="stretch">
          {/* Top Section */}
          <Flex
            direction={['column', 'row']}
            justify="space-between"
            align={['flex-start', 'center']}
            gap={['1rem', '2rem']}
          >
            {/* Logo */}
            <Box maxW={['300px', '350px']} w="100%">
              <Link to={WWW_ROOT}>
                <Logo width={117} height={24} alt="Everlywell logo" />
              </Link>
            </Box>

            {/* CTA Button */}
            <Button
              as={Link}
              to="/contact-us"
              maxW={['300px', '350px']}
              height="3.25rem"
              px="1.5rem"
              mr="2.25rem"
              bg="#012b3b"
              color="white"
              borderRadius="1.75rem"
              _hover={{ bg: '#023d52' }}
              rightIcon={
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20.7806 12.5306L14.0306 19.2806C13.8899 19.4213 13.699 19.5003 13.5 19.5003C13.301 19.5003 13.1101 19.4213 12.9694 19.2806C12.8286 19.1398 12.7496 18.949 12.7496 18.7499C12.7496 18.5509 12.8286 18.36 12.9694 18.2193L18.4397 12.7499H3.75C3.55109 12.7499 3.36032 12.6709 3.21967 12.5303C3.07902 12.3896 3 12.1988 3 11.9999C3 11.801 3.07902 11.6103 3.21967 11.4696C3.36032 11.3289 3.55109 11.2499 3.75 11.2499H18.4397L12.9694 5.78055C12.8286 5.63982 12.7496 5.44895 12.7496 5.24993C12.7496 5.05091 12.8286 4.86003 12.9694 4.7193C13.1101 4.57857 13.301 4.49951 13.5 4.49951C13.699 4.49951 13.8899 4.57857 14.0306 4.7193L20.7806 11.4693C20.8504 11.539 20.9057 11.6217 20.9434 11.7127C20.9812 11.8038 21.0006 11.9014 21.0006 11.9999C21.0006 12.0985 20.9812 12.1961 20.9434 12.2871C20.9057 12.3782 20.8504 12.4609 20.7806 12.5306Z"
                    fill="white"
                  />
                </svg>
              }
            >
              Find your solution
            </Button>
          </Flex>

          {/* Middle Section */}
          <Flex
            direction={['column', 'column', 'row']}
            justify="space-between"
            gap={['1rem', '2rem']}
            py="25px"
          >
            {/* Navigation Links */}
            <Grid
              gridTemplateColumns={[
                'repeat(1, 1fr)',
                'repeat(2, 1fr)',
                'repeat(4, 1fr)',
              ]}
              gap={['2rem', '4rem']}
              mb={['2rem', '2rem', 0]}
            >
              {menuItems.map((item, index) => (
                <VStack key={index} align="flex-start" spacing="1.4rem">
                  <Text
                    color="gray.500"
                    fontSize={['0.875rem', '1rem']}
                    fontWeight="500"
                  >
                    {item.title}
                  </Text>
                  {item.submenu.map((subItem) => (
                    <Box key={subItem.link}>
                      <Link to={subItem.link}>
                        <Text
                          color="#012b3b"
                          fontSize={['0.875rem', '1rem']}
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {subItem.title}
                        </Text>
                      </Link>
                    </Box>
                  ))}
                </VStack>
              ))}
            </Grid>

            {/* Social and Info */}
            <VStack align="flex-start" spacing="1rem">
              <Text
                color="gray.500"
                fontSize={['0.875rem', '1rem']}
                fontWeight="500"
              >
                Follow us
              </Text>
              <HStack spacing="1.25rem" py="0.5rem">
                <a
                  href="https://instagram.com/everlywell/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <InstagramIcon width={20} height={20} />
                </a>
                <a
                  href="https://www.facebook.com/everlywell"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FacebookIcon width={20} height={20} />
                </a>
                <a
                  href="https://www.linkedin.com/company/everlywell/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkedInIcon width={20} height={20} />
                </a>
              </HStack>

              <VStack spacing="0.5rem" maxW="15.625rem" align="flex-start">
                <Text
                  color="gray.500"
                  fontSize={['0.6rem', '0.875rem']}
                  lineHeight="1.15"
                  whiteSpace="pre-line"
                >
                  {
                    currentPage?.metadata?.description?.childMarkdownRemark
                      ?.rawMarkdownBody
                  }
                </Text>
              </VStack>

              <VStack align="flex-start" spacing="0.5rem">
                <a
                  href="https://www.everlywell.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Text
                    color="#012b3b"
                    fontSize={['0.875rem', '1rem']}
                    _hover={{ textDecoration: 'underline' }}
                  >
                    everlywell.com
                  </Text>
                </a>
              </VStack>
            </VStack>
          </Flex>

          {/* Bottom Section */}
          <Flex
            direction={['column', 'row']}
            justify="space-between"
            align="center"
            pt="1rem"
            borderTop="1px solid"
            borderColor="#e8e8e2"
          >
            <HStack spacing="1rem" mb={['0.5rem', 0]}>
              <Link to="/privacy-policy">
                <Text
                  color="gray.500"
                  fontSize={['0.75rem', '0.875rem']}
                  _hover={{ textDecoration: 'underline' }}
                >
                  Privacy Policy
                </Text>
              </Link>
              <Link to="/terms-of-use">
                <Text
                  color="gray.500"
                  fontSize={['0.75rem', '0.875rem']}
                  _hover={{ textDecoration: 'underline' }}
                >
                  Terms of Use
                </Text>
              </Link>
            </HStack>
            <Text color="gray.500" fontSize={['0.75rem', '0.875rem']}>
              © Everlywell 2025
            </Text>
          </Flex>
        </VStack>
      </Box>
    </Box>
  );
};

export default Footer;
